import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import MobileLayout from '../MobileLayout';
import {
  FulfillmentLineExceptionType,
  WaveItem,
  WaveResponse,
} from '../../../services/fulfillment/types';
import {
  cancelWave,
  completeWave,
  getActiveWave,
  reportPickException,
  scanPick,
  scanQc,
} from '../../../services/fulfillment/waveApi';

const EXCEPTION_LABELS: Record<FulfillmentLineExceptionType, string> = {
  item_not_found: 'Item Not Found',
  item_damaged: 'Item Damaged',
  skip: 'Skip',
  other: 'Other',
};

const isBikeSku = (sku: string) => sku.charAt(0).toUpperCase() === 'B';
const isFrameSku = (sku: string) => sku.charAt(0).toUpperCase() === 'F';
const isWheelSku = (sku: string) => sku.charAt(0).toUpperCase() === 'W';

const itemTypePriority = (item: WaveItem): number => {
  const first = item.sku.charAt(0).toUpperCase();
  if (first === 'F') return 2;
  if (first === 'W') return 3;
  if (first === 'B') return 4;
  return 1;
};

const isDoneItem = (i: WaveItem) =>
  i.waveOrderStatus === 'picked' ||
  i.waveOrderStatus === 'cancelled' ||
  ['quality-check', 'picked', 'packed', 'exception'].includes(i.lineStatus ?? '');

const getCarryMessage = (orderItems: WaveItem[]): string | null => {
  const hasPaa = orderItems.some(
    i => !isFrameSku(i.sku) && !isWheelSku(i.sku) && !isBikeSku(i.sku),
  );
  if (!hasPaa) return null;
  if (orderItems.some(i => isFrameSku(i.sku))) return 'Put PA&A items with the frame';
  if (orderItems.some(i => isWheelSku(i.sku))) return 'Put PA&A items with the wheel';
  return 'Put PA&A items with the bike';
};

type PickStep =
  | { name: 'loading' }
  | { name: 'pick_item'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'move_to_bin'; item: WaveItem; itemIndex: number; totalItems: number }
  | { name: 'order_complete'; orderName: string }
  | { name: 'wave_done'; completedOrders: number };

interface LocationState {
  wave?: WaveResponse;
}

const MixedWavePickFlow: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { waveId } = useParams<{ waveId: string }>();

  const [step, setStep] = useState<PickStep>({ name: 'loading' });
  const [wave, setWave] = useState<WaveResponse | null>(location.state?.wave ?? null);
  const [scanInput, setScanInput] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
  const [
    selectedExceptionType,
    setSelectedExceptionType,
  ] = useState<FulfillmentLineExceptionType | null>(null);
  const [exceptionNotes, setExceptionNotes] = useState('');
  const [submittingException, setSubmittingException] = useState(false);
  const [skippedLineItemGids, setSkippedLineItemGids] = useState<Set<string>>(new Set());

  const deriveStep = useCallback(
    (w: WaveResponse): PickStep => {
      const incompleteOrderIds = Array.from(
        new Set(
          w.items
            .filter(i => i.waveOrderStatus !== 'picked' && i.waveOrderStatus !== 'cancelled')
            .map(i => i.waveOrderId),
        ),
      );
      if (incompleteOrderIds.length === 0) {
        return { name: 'wave_done', completedOrders: w.completedOrders };
      }

      const currentOrderId = incompleteOrderIds[0];
      const orderItems = w.items.filter(i => i.waveOrderId === currentOrderId);
      const unpicked = orderItems
        .filter(i => !isDoneItem(i))
        .sort((a, b) => {
          const aSkipped = skippedLineItemGids.has(a.shopifyLineItemGid);
          const bSkipped = skippedLineItemGids.has(b.shopifyLineItemGid);
          if (aSkipped !== bSkipped) return aSkipped ? 1 : -1;
          return itemTypePriority(a) - itemTypePriority(b);
        });

      if (unpicked.length === 0) {
        return { name: 'wave_done', completedOrders: w.completedOrders };
      }

      const item = unpicked[0];
      const sortedAll = [...orderItems].sort((a, b) => itemTypePriority(a) - itemTypePriority(b));
      const itemIndex =
        sortedAll.findIndex(i => i.shopifyLineItemGid === item.shopifyLineItemGid) + 1;
      return { name: 'pick_item', item, itemIndex, totalItems: orderItems.length };
    },
    [skippedLineItemGids],
  );

  useEffect(() => {
    if (wave) {
      setStep(deriveStep(wave));
      return;
    }
    getActiveWave()
      .then(w => {
        if (w) {
          setWave(w);
          setStep(deriveStep(w));
        } else {
          history.push('/mobile/pick');
        }
      })
      .catch(() => history.push('/mobile/pick'));
  }, [history, deriveStep, wave]);

  // Re-derive step when skipped set changes (deriveStep deps update triggers this via wave useEffect)
  useEffect(() => {
    if (wave && step.name === 'pick_item') {
      setStep(deriveStep(wave));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skippedLineItemGids]);

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handlePickScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'pick_item') return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    const result = await scanPick(Number(waveId), step.item.shopifyLineItemGid, value);
    if ('error' in result && result.error === 'sku_mismatch') {
      setScanError('SKU does not match. Please scan the correct item.');
      triggerShake();
      focusInput();
      return;
    }
    setStep({
      name: 'move_to_bin',
      item: step.item,
      itemIndex: step.itemIndex,
      totalItems: step.totalItems,
    });
    setScanInput('');
    focusInput();
  };

  const handleBinScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || step.name !== 'move_to_bin') return;
    const value = scanInput.trim();
    setScanInput('');
    setScanError(null);
    if (!value) return focusInput();

    const result = await scanQc(Number(waveId), step.item.shopifyLineItemGid, value);
    if ('error' in result) {
      setScanError(
        isBikeSku(step.item.sku)
          ? wave?.qcAllowRefurbishedZone
            ? 'Invalid QC bin. Please scan a valid operations or refurbished-goods bin.'
            : 'Invalid QC bin. Please scan a valid operations bin.'
          : 'Invalid bin. Please scan a valid staging bin.',
      );
      focusInput();
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    setScanInput('');

    const currentItem = step.item;
    const waveOrderPicked = updatedWave.items.some(
      i => i.waveOrderId === currentItem.waveOrderId && i.waveOrderStatus === 'picked',
    );
    if (waveOrderPicked) {
      setStep({ name: 'order_complete', orderName: currentItem.shopifyOrderName });
    } else {
      const nextStep = deriveStep(updatedWave);
      setStep(nextStep);
      if (nextStep.name === 'pick_item') focusInput();
    }
  };

  const handleSkip = () => {
    if (step.name !== 'pick_item' || !wave) return;
    const orderItems = wave.items.filter(i => i.waveOrderId === step.item.waveOrderId);
    const unpickedCount = orderItems.filter(i => !isDoneItem(i)).length;
    if (unpickedCount <= 1) return;
    setSkippedLineItemGids(prev => new Set(Array.from(prev).concat(step.item.shopifyLineItemGid)));
  };

  const handleNextOrder = () => {
    if (!wave) return;
    setScanError(null);
    const nextStep = deriveStep(wave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handleOpenException = () => {
    setSelectedExceptionType(null);
    setExceptionNotes('');
    setExceptionModalOpen(true);
  };

  const handleSubmitException = async () => {
    if (step.name !== 'pick_item' || !selectedExceptionType) return;
    setSubmittingException(true);
    const result = await reportPickException(
      Number(waveId),
      step.item.shopifyLineItemGid,
      selectedExceptionType,
      exceptionNotes || null,
    );
    setSubmittingException(false);
    if ('error' in result) {
      setScanError('Failed to submit exception. Please try again.');
      setExceptionModalOpen(false);
      return;
    }
    const updatedWave = result as WaveResponse;
    setWave(updatedWave);
    setExceptionModalOpen(false);
    setScanError(null);
    const nextStep = deriveStep(updatedWave);
    setStep(nextStep);
    if (nextStep.name === 'pick_item') focusInput();
  };

  const handleReleaseRemaining = async () => {
    await cancelWave(Number(waveId));
    history.push('/mobile/pick');
  };

  const handleWaveDone = async () => {
    try {
      await completeWave(Number(waveId));
    } catch {
      // best-effort
    }
    history.push('/mobile/pick');
  };

  const renderOrderCard = (item: WaveItem) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">
          Order
        </Typography>
        <Typography variant="h6">{item.shopifyOrderName}</Typography>
        <Typography variant="body2">{item.customerName}</Typography>
      </CardContent>
    </Card>
  );

  const renderItemCard = (
    item: WaveItem,
    itemIndex: number,
    totalItems: number,
    onSkip?: () => void,
    skipDisabled?: boolean,
    onException?: () => void,
  ) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {itemIndex} of {totalItems} Items
          </Typography>
          <Box display="flex" alignItems="center">
            {onSkip && (
              <IconButton
                size="small"
                onClick={onSkip}
                disabled={skipDisabled}
                sx={{ p: 0.5, mr: 0.5 }}
                aria-label="Skip item"
              >
                <SkipNextIcon style={{ fontSize: 28, color: skipDisabled ? '#ccc' : '#AAAAAA' }} />
              </IconButton>
            )}
            {onException && (
              <IconButton
                size="small"
                onClick={onException}
                sx={{ p: 0.5, ml: 1 }}
                aria-label="Report issue"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="28px"
                  viewBox="0 0 24 24"
                  width="28px"
                  fill="#AAAAAA"
                  style={{ flexShrink: 0 }}
                >
                  <g>
                    <rect fill="none" height="24" width="24" />
                    <path d="M2.81,2.81L1.39,4.22L7,9.83l0,4.3l-2.6-1.48c-0.17-0.09-0.34-0.14-0.54-0.14c-0.26,0-0.5,0.09-0.7,0.26L2,13.88l6.8,7.18 c0.57,0.6,1.35,0.94,2.18,0.94H17c0.62,0,1.18-0.19,1.66-0.52l1.12,1.12l1.41-1.41L2.81,2.81z M17,20h-6 c-0.39,0-0.64-0.23-0.75-0.36L6.87,16H9l0-4.17l8.14,8.14C17.09,19.98,17.05,20,17,20z M13.83,11H14V3.25C14,2.56,14.56,2,15.25,2 s1.25,0.56,1.25,1.25V11h1V5.25C17.5,4.56,18.06,4,18.75,4S20,4.56,20,5.25v11.92l-2-2V13h-2.17L13.83,11z M13,10.17V2.25 C13,1.56,12.44,1,11.75,1S10.5,1.56,10.5,2.25v5.42L13,10.17z M9.5,6.67V4.25C9.5,3.56,8.94,3,8.25,3c-0.67,0-1.2,0.53-1.24,1.18v0 L9.5,6.67z" />
                  </g>
                </svg>
              </IconButton>
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Typography variant="h5" fontWeight="bold">
            {item.sku}
          </Typography>
          <Typography variant="h5" color="primary">
            {item.binLocations[0]?.binCode ?? '—'}
          </Typography>
        </Box>
        {item.imageUrl && (
          <Box mt={1} display="flex" justifyContent="center">
            <img
              src={item.imageUrl}
              alt={item.title ?? item.sku}
              style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain' }}
            />
          </Box>
        )}
        {item.title && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {item.title}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderExceptionModal = (item: WaveItem) => (
    <Dialog fullScreen open={exceptionModalOpen} onClose={() => setExceptionModalOpen(false)}>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={() => setExceptionModalOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            Report an Issue
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {item.sku} — {item.shopifyOrderName}
        </Typography>
        <Typography variant="subtitle2" mb={1}>
          What&apos;s the issue?
        </Typography>
        {(Object.keys(EXCEPTION_LABELS) as FulfillmentLineExceptionType[]).map(type => (
          <Button
            key={type}
            variant={selectedExceptionType === type ? 'contained' : 'outlined'}
            color={selectedExceptionType === type ? 'error' : 'inherit'}
            fullWidth
            sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
            onClick={() => setSelectedExceptionType(type)}
          >
            {EXCEPTION_LABELS[type]}
          </Button>
        ))}
        <TextField
          label="Notes (optional)"
          multiline
          rows={3}
          fullWidth
          value={exceptionNotes}
          onChange={e => setExceptionNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
        <Button
          variant="contained"
          color="error"
          fullWidth
          disabled={!selectedExceptionType || submittingException}
          onClick={handleSubmitException}
          sx={{ mt: 3 }}
        >
          {submittingException ? 'Submitting…' : 'Submit Exception'}
        </Button>
      </Box>
    </Dialog>
  );

  const renderScanInput = (
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void,
    label: string,
  ) => (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: '1px solid #ddd',
        p: 2,
        maxWidth: 480,
        margin: '0 auto',
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      <input
        ref={inputRef}
        value={scanInput}
        onChange={e => setScanInput(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
        style={{
          width: '100%',
          fontSize: 18,
          padding: '10px 12px',
          border: shake ? '2px solid red' : '2px solid #1976d2',
          borderRadius: 6,
          outline: 'none',
          boxSizing: 'border-box',
          animation: shake ? 'shake 0.4s ease' : 'none',
        }}
        placeholder="Scan barcode..."
      />
      {scanError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {scanError}
        </Alert>
      )}
    </Box>
  );

  if (step.name === 'loading') {
    return (
      <MobileLayout>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </MobileLayout>
    );
  }

  if (step.name === 'pick_item') {
    const orderItems = wave?.items.filter(i => i.waveOrderId === step.item.waveOrderId) ?? [];
    const unpickedCount = orderItems.filter(i => !isDoneItem(i)).length;
    return (
      <MobileLayout>
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          {renderItemCard(
            step.item,
            step.itemIndex,
            step.totalItems,
            handleSkip,
            unpickedCount <= 1,
            handleOpenException,
          )}
        </Box>
        {renderScanInput(handlePickScan, 'Scan to Pick')}
        {renderExceptionModal(step.item)}
      </MobileLayout>
    );
  }

  if (step.name === 'move_to_bin') {
    const isBike = isBikeSku(step.item.sku);
    const orderItems = wave?.items.filter(i => i.waveOrderId === step.item.waveOrderId) ?? [];
    const carryMessage = itemTypePriority(step.item) > 1 ? getCarryMessage(orderItems) : null;
    return (
      <MobileLayout>
        <Box pb={14} pt={1}>
          {renderOrderCard(step.item)}
          {carryMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{carryMessage}</strong>
            </Alert>
          )}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                {step.itemIndex} of {step.totalItems} Items
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="h5" fontWeight="bold">
                  {step.item.sku}
                </Typography>
                <Typography variant="h6" color="secondary">
                  {isBike ? 'Move to QC' : 'Move to Staging'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {renderScanInput(handleBinScan, isBike ? 'Scan to QC Location' : 'Scan to Staging Bin')}
      </MobileLayout>
    );
  }

  if (step.name === 'order_complete') {
    return (
      <MobileLayout>
        <Box display="flex" flexDirection="column" alignItems="center" pt={4} pb={4}>
          <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            Order Complete
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            {step.orderName}
          </Typography>
          <Button variant="contained" fullWidth sx={{ mb: 2 }} onClick={handleNextOrder}>
            Next Order
          </Button>
          <Button variant="outlined" color="error" fullWidth onClick={handleReleaseRemaining}>
            Release Remaining Orders
          </Button>
        </Box>
      </MobileLayout>
    );
  }

  // wave_done
  return (
    <MobileLayout>
      <Box display="flex" flexDirection="column" alignItems="center" pt={4}>
        <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
        <Typography variant="h5" mt={2} mb={1}>
          All Mixed Orders Picked!
        </Typography>
        <Card sx={{ width: '100%', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{step.completedOrders}</Typography>
            <Typography variant="body2" color="text.secondary">
              Orders Processed
            </Typography>
          </CardContent>
        </Card>
        <Button variant="contained" fullWidth onClick={handleWaveDone}>
          Return Home
        </Button>
      </Box>
    </MobileLayout>
  );
};

export default MixedWavePickFlow;
